/**
 * DashboardPage.tsx — réplica do dashboard.html original:
 * saudação personalizada + relógio ao vivo, Ações Rápidas, barra de
 * busca/filtro, Pipeline de Processos com progresso geral, os 5 cards
 * "premium" de etapa com conectores, e a lista unificada de itens.
 */

import {
  faBalanceScale,
  faBolt,
  faCheck,
  faChevronRight,
  faClipboardCheck,
  faClock,
  faCrown,
  faFileAlt,
  faFileContract,
  faFilter,
  faFlagCheckered,
  faGavel,
  faHospitalUser,
  faInbox,
  faLayerGroup,
  faPlus,
  faRocket,
  faSearch,
  faSearchDollar,
  faShieldAlt,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useOutletContext } from 'react-router'
import type { Sessao } from '../../api/auth'
import { listarItens, resumoDashboard } from '../../api/dashboard'
import { useSaudacao } from '../../hooks/useSaudacao'
import { temPermissao } from '../../lib/sessao'
import { CardsJuridicos } from './widgets/CardsJuridicos'
import { CardWidget } from './widgets/CardWidget'
import { Calendario } from './widgets/Calendario'
import { ResultadosAuditoria, StatusProcessos } from './widgets/Graficos'
import { PesquisasJuridicas, UltimasDefesas } from './widgets/ListasLaterais'

// ── Ações rápidas (mesma ordem/cores do original) ────────────────────────
const ACOES: {
  rotulo: string
  url: string
  icone: IconDefinition
  permissao: string
  fundo: string
  circulo: string
  borda: string
}[] = [
  {
    rotulo: 'Novo Processo', url: '/app/dfd/novo', icone: faPlus, permissao: 'processo_licitatorio',
    fundo: 'from-blue-50 to-indigo-100 hover:from-blue-100 hover:to-indigo-200',
    circulo: 'from-blue-500 to-indigo-600', borda: 'border-blue-200',
  },
  {
    rotulo: 'Nova Auditoria', url: '/audit', icone: faSearchDollar, permissao: 'auditoria',
    fundo: 'from-teal-50 to-emerald-100 hover:from-teal-100 hover:to-emerald-200',
    circulo: 'from-teal-500 to-emerald-600', borda: 'border-teal-200',
  },
  {
    rotulo: 'Pesquisa Jurídica', url: '/legal_research', icone: faBalanceScale, permissao: 'pesquisa_juridica',
    fundo: 'from-purple-50 to-violet-100 hover:from-purple-100 hover:to-violet-200',
    circulo: 'from-purple-500 to-violet-600', borda: 'border-purple-200',
  },
  {
    rotulo: 'Nova Impugnação', url: '/juridico/impugnacao', icone: faGavel, permissao: 'impugnacao',
    fundo: 'from-rose-50 to-red-100 hover:from-rose-100 hover:to-red-200',
    circulo: 'from-rose-500 to-red-600', borda: 'border-rose-200',
  },
  {
    rotulo: 'Nova Defesa', url: '/juridico/defesa', icone: faShieldAlt, permissao: 'defesa',
    fundo: 'from-amber-50 to-orange-100 hover:from-amber-100 hover:to-orange-200',
    circulo: 'from-amber-500 to-orange-600', borda: 'border-amber-200',
  },
  {
    rotulo: 'Auditoria SUS', url: '/auditoria-sus/', icone: faHospitalUser, permissao: 'auditoria_sus',
    fundo: 'from-cyan-50 to-sky-100 hover:from-cyan-100 hover:to-sky-200',
    circulo: 'from-cyan-500 to-sky-600', borda: 'border-cyan-200',
  },
]

const TIPOS_ITEM: Record<string, { rotulo: string; tag: string; fundo: string; icone: IconDefinition; circulo: string }> = {
  processo: {
    rotulo: 'Processo', tag: 'bg-indigo-100 text-indigo-700', icone: faFileContract,
    circulo: 'from-indigo-500 to-purple-600',
    fundo: 'from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 border-indigo-200',
  },
  auditoria: {
    rotulo: 'Auditoria', tag: 'bg-teal-100 text-teal-700', icone: faSearchDollar,
    circulo: 'from-teal-500 to-emerald-600',
    fundo: 'from-teal-50 to-emerald-50 hover:from-teal-100 hover:to-emerald-100 border-teal-200',
  },
  pesquisa_juridica: {
    rotulo: 'Pesquisa', tag: 'bg-purple-100 text-purple-700', icone: faSearch,
    circulo: 'from-purple-500 to-violet-600',
    fundo: 'from-purple-50 to-violet-50 hover:from-purple-100 hover:to-violet-100 border-purple-200',
  },
  impugnacao: {
    rotulo: 'Impugnação', tag: 'bg-rose-100 text-rose-700', icone: faGavel,
    circulo: 'from-rose-500 to-pink-600',
    fundo: 'from-rose-50 to-pink-50 hover:from-rose-100 hover:to-pink-100 border-rose-200',
  },
  defesa: {
    rotulo: 'Defesa', tag: 'bg-amber-100 text-amber-700', icone: faShieldAlt,
    circulo: 'from-amber-500 to-orange-600',
    fundo: 'from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 border-amber-200',
  },
}

const OPCOES_FILTRO = [
  { valor: 'all', rotulo: 'Todos os Tipos' },
  { valor: 'processo', rotulo: 'Apenas Processos' },
  { valor: 'auditoria', rotulo: 'Apenas Auditorias' },
  { valor: 'pesquisa_juridica', rotulo: 'Apenas Pesquisas Jurídicas' },
  { valor: 'juridico', rotulo: 'Apenas Peças Jurídicas' },
  { valor: 'impugnacao', rotulo: 'Apenas Impugnações' },
  { valor: 'defesa', rotulo: 'Apenas Defesas' },
]

function pct(numerador: number, denominador: number) {
  return denominador > 0 ? Math.round((numerador / denominador) * 100) : 0
}

// ── Saudação + relógio ────────────────────────────────────────────────────
function Saudacao({ sessao }: { sessao: Sessao }) {
  const { saudacao, emoji, mensagem, hora, data } = useSaudacao()
  const primeiroNome = (sessao.usuario.nome ?? sessao.usuario.email).split(/[ @]/)[0]

  return (
    <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-center">
      <div className="flex items-center gap-3">
        <span className="text-4xl">{emoji}</span>
        <div>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-white">
            {saudacao}, {primeiroNome}!
          </h1>
          <div className="mt-1 flex items-center gap-2">
            <p className="text-slate-500 dark:text-slate-400">{mensagem}</p>
            <span className="h-1 w-1 rounded-full bg-slate-300" />
            <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">{data}</p>
          </div>
        </div>
      </div>
      <div className="group flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-3 shadow-sm transition-all duration-300 hover:shadow-md dark:border-slate-700 dark:bg-dark-card">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition-transform group-hover:scale-110 dark:bg-indigo-900/30 dark:text-indigo-400">
          <FontAwesomeIcon icon={faClock} className="text-lg" />
        </div>
        <div className="pr-2">
          <div className="font-mono text-2xl font-bold tracking-wider text-slate-800 dark:text-white">
            {hora}
          </div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            Horário de Brasília
          </div>
        </div>
      </div>
    </div>
  )
}

function AcoesRapidas({ sessao }: { sessao: Sessao }) {
  const disponiveis = ACOES.filter((a) => temPermissao(sessao, a.permissao))
  if (disponiveis.length === 0) return null

  return (
    <div className="mb-8 rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-1 shadow-lg">
      <div className="rounded-xl bg-white p-4 dark:bg-dark-card">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
          <FontAwesomeIcon icon={faBolt} className="mr-2 text-amber-500" />
          Ações Rápidas
        </h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
          {disponiveis.map((a) => (
            <a
              key={a.rotulo}
              href={a.url}
              className={`group flex flex-col items-center justify-center rounded-xl border bg-gradient-to-br p-4 transition-all duration-300 hover:scale-105 hover:shadow-md ${a.fundo} ${a.borda}`}
            >
              <div
                className={`mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br shadow-md transition-transform duration-300 group-hover:scale-110 ${a.circulo}`}
              >
                <FontAwesomeIcon icon={a.icone} className="text-lg text-white" />
              </div>
              <span className="text-center text-sm font-medium text-slate-700">{a.rotulo}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Card de etapa do pipeline ─────────────────────────────────────────────
function CardEtapa({
  numero, icone, badge, iconeFundo, numeroGradiente, borda, glow, valor, rotulo,
  legenda, aguardando, progresso, barra, concluido, conector, miniStats,
}: {
  numero: number
  icone: IconDefinition
  badge: string
  iconeFundo: string
  numeroGradiente: string
  borda: string
  glow: string
  valor: number
  rotulo: string
  legenda: string
  aguardando?: number
  progresso?: number
  barra?: string
  concluido: boolean
  conector?: string
  miniStats?: { valor: number; rotulo: string }[]
}) {
  return (
    <div className="group relative">
      <div className={`absolute inset-0 rounded-2xl opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-20 ${glow}`} />
      <div className={`relative overflow-hidden rounded-2xl border bg-white p-5 shadow-lg transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl dark:bg-dark-card ${borda}`}>
        <div className="absolute right-3 top-3">
          <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${badge}`}>
            {numero}
          </span>
        </div>
        <div className="relative mb-4">
          <div className={`flex h-14 w-14 items-center justify-center rounded-xl shadow-lg transition-transform duration-500 group-hover:scale-110 ${iconeFundo}`}>
            <FontAwesomeIcon icon={icone} className="text-xl text-white" />
          </div>
          {concluido && (
            <div className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-green-500 dark:border-dark-card">
              <FontAwesomeIcon icon={faCheck} className="text-[8px] text-white" />
            </div>
          )}
        </div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
          {rotulo}
        </p>
        <h3 className={`mb-2 bg-gradient-to-r bg-clip-text text-4xl font-black tabular-nums text-transparent ${numeroGradiente}`}>
          {valor}
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">{legenda}</p>

        {miniStats && (
          <div className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-100 pt-4 dark:border-slate-700/50">
            {miniStats.map((m) => (
              <div key={m.rotulo} className="rounded-lg bg-slate-50 p-2 text-center dark:bg-slate-700/30">
                <p className="text-lg font-bold text-slate-700 dark:text-slate-200">{m.valor}</p>
                <p className="text-[10px] text-slate-400">{m.rotulo}</p>
              </div>
            ))}
          </div>
        )}

        {aguardando !== undefined && progresso !== undefined && (
          <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-700/50">
            <div className="mb-2 flex items-center justify-between text-xs">
              <span className="text-slate-500 dark:text-slate-400">Próxima etapa</span>
              <span className="font-bold text-slate-600 dark:text-slate-300">{aguardando} aguardando</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700/50">
              <div className={`h-full rounded-full ${barra}`} style={{ width: `${progresso}%` }} />
            </div>
          </div>
        )}
      </div>
      {conector && (
        <div className={`absolute -right-2 top-1/2 z-20 hidden h-4 w-4 -translate-y-1/2 items-center justify-center rounded-full lg:flex ${conector}`}>
          <FontAwesomeIcon icon={faChevronRight} className="text-[8px] text-white" />
        </div>
      )}
    </div>
  )
}

function Pipeline() {
  const { data: resumo } = useQuery({
    queryKey: ['dashboard-resumo'],
    queryFn: resumoDashboard,
    staleTime: 30_000,
  })
  if (!resumo) return null
  const p = resumo.processos
  const progressoGeral = pct(p.com_edital, p.total)

  return (
    <div className="mb-8">
      <div className="mb-5 rounded-2xl border border-slate-200/50 bg-white p-5 shadow-xl shadow-slate-200/50 backdrop-blur-xl dark:border-slate-700/50 dark:bg-dark-card dark:shadow-slate-900/50">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 shadow-lg shadow-indigo-500/30">
                <FontAwesomeIcon icon={faRocket} className="text-xl text-white" />
              </div>
              <div className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-green-500 dark:border-dark-card">
                <FontAwesomeIcon icon={faCheck} className="text-[8px] text-white" />
              </div>
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800 dark:text-white">Pipeline de Processos</h2>
              <p className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                  <FontAwesomeIcon icon={faLayerGroup} className="mr-1" /> {p.total} processos
                </span>
                <span className="text-slate-300 dark:text-slate-600">•</span>
                <span className="font-medium text-emerald-600 dark:text-emerald-400">
                  {progressoGeral}% concluídos
                </span>
              </p>
            </div>
          </div>
          <div className="max-w-md flex-1">
            <div className="mb-2 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
              <span>Progresso Geral</span>
              <span className="font-bold text-indigo-600 dark:text-indigo-400">
                {p.com_edital} de {p.total} finalizados
              </span>
            </div>
            <div className="h-3 overflow-hidden rounded-full bg-slate-100 shadow-inner dark:bg-slate-700">
              <div
                className="relative h-full rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 transition-all duration-1000 ease-out"
                style={{ width: `${progressoGeral}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/30 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative">
        <div className="absolute left-0 right-0 top-1/2 z-0 hidden h-1 -translate-y-1/2 rounded-full bg-gradient-to-r from-slate-200 via-amber-200 to-purple-200 dark:from-slate-700 dark:via-amber-800 dark:to-purple-800 lg:block" />
        <div className="relative z-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <CardEtapa
            numero={0} icone={faInbox}
            badge="bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
            iconeFundo="bg-gradient-to-br from-slate-400 to-slate-600"
            numeroGradiente="from-slate-700 to-slate-900 dark:from-slate-200 dark:to-white"
            borda="border-slate-200 dark:border-slate-700"
            glow="bg-gradient-to-br from-slate-400 to-slate-600"
            valor={p.total} rotulo="Total" legenda="processos criados"
            concluido={false} conector="bg-slate-400"
            miniStats={[
              { valor: p.total - p.com_dfd, rotulo: 'Iniciando' },
              { valor: p.com_dfd, rotulo: 'Em andamento' },
            ]}
          />
          <CardEtapa
            numero={1} icone={faFileAlt}
            badge="bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400"
            iconeFundo="bg-gradient-to-br from-blue-500 to-indigo-600 shadow-blue-500/30"
            numeroGradiente="from-blue-600 to-indigo-600"
            borda="border-blue-200 dark:border-blue-800/50"
            glow="bg-gradient-to-br from-blue-400 to-indigo-600"
            valor={p.com_dfd} rotulo="DFD" legenda="Documento de Demanda"
            aguardando={p.com_dfd - p.com_dfd_etp} progresso={pct(p.com_dfd_etp, p.com_dfd)}
            barra="bg-gradient-to-r from-blue-500 to-indigo-500"
            concluido={p.com_dfd > 0} conector="bg-gradient-to-r from-blue-500 to-amber-500"
          />
          <CardEtapa
            numero={2} icone={faClipboardCheck}
            badge="bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-400"
            iconeFundo="bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-500/30"
            numeroGradiente="from-amber-600 to-orange-600"
            borda="border-amber-200 dark:border-amber-800/50"
            glow="bg-gradient-to-br from-amber-400 to-orange-600"
            valor={p.com_dfd_etp} rotulo="ETP" legenda="Estudo Técnico"
            aguardando={p.com_dfd_etp - p.com_tr} progresso={pct(p.com_tr, p.com_dfd_etp)}
            barra="bg-gradient-to-r from-amber-500 to-orange-500"
            concluido={p.com_dfd_etp > 0} conector="bg-gradient-to-r from-amber-500 to-emerald-500"
          />
          <CardEtapa
            numero={3} icone={faFileContract}
            badge="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-400"
            iconeFundo="bg-gradient-to-br from-emerald-500 to-green-600 shadow-emerald-500/30"
            numeroGradiente="from-emerald-600 to-green-600"
            borda="border-emerald-200 dark:border-emerald-800/50"
            glow="bg-gradient-to-br from-emerald-400 to-green-600"
            valor={p.com_tr} rotulo="TR" legenda="Termo de Referência"
            aguardando={p.com_tr - p.com_edital} progresso={pct(p.com_edital, p.com_tr)}
            barra="bg-gradient-to-r from-emerald-500 to-green-500"
            concluido={p.com_tr > 0} conector="bg-gradient-to-r from-emerald-500 to-purple-500"
          />

          <div className="group relative">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-400 to-violet-600 opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-30" />
            <div className="relative overflow-hidden rounded-2xl border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-violet-50 p-5 shadow-lg transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl dark:border-purple-700 dark:from-purple-900/20 dark:to-violet-900/20">
              <div className="absolute right-3 top-3">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-xs font-bold text-white shadow-lg">
                  <FontAwesomeIcon icon={faCrown} />
                </span>
              </div>
              <div className="relative mb-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 via-violet-500 to-fuchsia-500 shadow-lg ring-4 ring-purple-200 transition-transform duration-500 group-hover:scale-110 dark:ring-purple-800">
                  <FontAwesomeIcon icon={faGavel} className="text-xl text-white" />
                </div>
                {p.com_edital > 0 && (
                  <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-gradient-to-r from-green-400 to-emerald-500 shadow-lg dark:border-dark-card">
                    <FontAwesomeIcon icon={faCheck} className="text-[10px] text-white" />
                  </div>
                )}
              </div>
              <div className="mb-1 flex items-center gap-2">
                <p className="text-xs font-bold uppercase tracking-wider text-purple-700 dark:text-purple-300">Edital</p>
                <span className="rounded-full bg-purple-200 px-2 py-0.5 text-[10px] font-bold text-purple-700 dark:bg-purple-800 dark:text-purple-300">
                  FASE FINAL
                </span>
              </div>
              <h3 className="mb-2 bg-gradient-to-r from-purple-600 via-violet-600 to-fuchsia-600 bg-clip-text text-4xl font-black tabular-nums text-transparent">
                {p.com_edital}
              </h3>
              <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Prontos para publicação</p>
              <div className="mt-4 border-t border-purple-200 pt-4 dark:border-purple-700/50">
                <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-2 dark:border-green-800/50 dark:from-green-900/20 dark:to-emerald-900/20">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-green-400 to-emerald-500 shadow-sm">
                    <FontAwesomeIcon icon={faFlagCheckered} className="text-xs text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-green-700 dark:text-green-400">Documentação Completa</p>
                    <p className="text-[10px] text-green-600 dark:text-green-500">DFD + ETP + TR + EDITAL</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface Filtro {
  busca: string
  dominio: string
}

function BarraFiltro({ aoAplicar }: { aoAplicar: (f: Filtro) => void }) {
  const [busca, setBusca] = useState('')
  const [dominio, setDominio] = useState('all')

  return (
    <>
      <div className="mb-8 rounded-xl bg-white p-4 shadow-md dark:bg-dark-card">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            aoAplicar({ busca: busca.trim(), dominio })
          }}
          className="flex flex-col items-center gap-4 md:flex-row"
        >
          <div className="w-full flex-grow">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
              </div>
              <input
                type="search"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por objeto ou nome do arquivo..."
                aria-label="Buscar por objeto ou nome do arquivo"
                className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 focus:border-indigo-500 focus:ring-indigo-500 dark:border-dark-border dark:bg-dark-bg dark:text-gray-100"
              />
            </div>
          </div>
          <select
            value={dominio}
            onChange={(e) => setDominio(e.target.value)}
            aria-label="Filtrar por tipo"
            className="block w-full flex-shrink-0 rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 md:w-auto dark:border-dark-border dark:bg-dark-bg dark:text-gray-100"
          >
            {OPCOES_FILTRO.map((o) => (
              <option key={o.valor} value={o.valor}>{o.rotulo}</option>
            ))}
          </select>
          <button
            type="submit"
            className="inline-flex w-full flex-shrink-0 items-center justify-center rounded-md bg-indigo-600 px-4 py-2 font-bold text-white hover:bg-indigo-700 md:w-auto"
          >
            <FontAwesomeIcon icon={faFilter} className="mr-2" />
            Filtrar
          </button>
        </form>
      </div>
    </>
  )
}

function ListaUnificada({ filtro }: { filtro: Filtro }) {
  const [pagina, setPagina] = useState(1)

  const consulta = useQuery({
    queryKey: ['dashboard-itens', filtro.busca, filtro.dominio, pagina],
    queryFn: () => listarItens({ busca: filtro.busca, dominio: filtro.dominio, pagina }),
  })

  return (
    <CardWidget
      titulo="Atividades Recentes"
      subtitulo="Últimas atualizações do sistema"
      icone={faClock}
      gradiente="from-blue-600 to-indigo-600"
      corSubtitulo="text-blue-100"
    >
      <div className="max-h-[560px] overflow-y-auto p-4">
        {consulta.isLoading && <p className="text-sm text-slate-500">Carregando…</p>}
        {consulta.data && (
          <>
            <ul className="space-y-3">
              {consulta.data.itens.map((item) => {
                const meta = TIPOS_ITEM[item.tipo]
                return (
                  <li key={`${item.tipo}-${item.id}`} className="group relative pl-6">
                    <div className={`absolute left-0 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-gradient-to-br shadow-lg ring-4 ring-white transition-transform duration-300 group-hover:scale-110 dark:ring-dark-card ${meta?.circulo ?? 'from-slate-400 to-slate-600'}`}>
                      <FontAwesomeIcon icon={meta?.icone ?? faFileContract} className="text-xs text-white" />
                    </div>
                    <a
                      href={item.url}
                      className={`ml-4 block rounded-xl border bg-gradient-to-r p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-md ${meta?.fundo ?? 'from-slate-50 to-slate-100 border-slate-200'}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-bold uppercase tracking-wider ${meta?.tag ?? 'bg-slate-100 text-slate-700'}`}>
                              {meta?.rotulo ?? item.tipo}
                            </span>
                            {item.situacao && (
                              <span className="rounded-full bg-white/70 px-2 py-0.5 text-xs font-medium text-slate-600">
                                {item.situacao}
                              </span>
                            )}
                          </div>
                          <h4 className="truncate text-sm font-semibold text-slate-800 md:text-base">
                            {item.titulo}
                          </h4>
                        </div>
                        <span className="shrink-0 text-xs text-slate-500">{item.data.slice(0, 10)}</span>
                      </div>
                    </a>
                  </li>
                )
              })}
              {consulta.data.itens.length === 0 && (
                <p className="py-6 text-center text-sm text-slate-500">Nenhum item encontrado.</p>
              )}
            </ul>
            {consulta.data.paginacao.total_paginas > 1 && (
              <div className="mt-4 flex items-center justify-center gap-3 text-sm">
                <button
                  disabled={pagina <= 1}
                  onClick={() => setPagina((v) => v - 1)}
                  className="rounded-lg bg-slate-100 px-3 py-1 disabled:opacity-40 dark:bg-dark-hover dark:text-gray-200"
                >
                  Anterior
                </button>
                <span className="text-slate-600 dark:text-slate-300">
                  {pagina} / {consulta.data.paginacao.total_paginas}
                </span>
                <button
                  disabled={pagina >= consulta.data.paginacao.total_paginas}
                  onClick={() => setPagina((v) => v + 1)}
                  className="rounded-lg bg-slate-100 px-3 py-1 disabled:opacity-40 dark:bg-dark-hover dark:text-gray-200"
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </CardWidget>
  )
}

export function DashboardPage() {
  const sessao = useOutletContext<Sessao>()
  const [filtro, setFiltro] = useState<Filtro>({ busca: '', dominio: 'all' })

  return (
    <div className="mx-auto max-w-7xl">
      <Saudacao sessao={sessao} />
      <AcoesRapidas sessao={sessao} />
      <BarraFiltro aoAplicar={setFiltro} />
      <Pipeline />
      <CardsJuridicos />

      {/* Duas colunas: timeline larga + coluna de widgets (como no original) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ListaUnificada filtro={filtro} />
        </div>
        <div className="space-y-6">
          <Calendario />
          <ResultadosAuditoria />
          <StatusProcessos />
          <UltimasDefesas />
          <PesquisasJuridicas />
        </div>
      </div>
    </div>
  )
}
